/**
 * Everything Quiz Buzz sends to phones, as pure functions of plain values.
 * DOM-free and free of runtime imports so a test can load it under plain Node.
 * Quiz has no spectator slots, so there are no spectator views.
 */
import type { ControllerSpec } from '@forgesworn/joystick-host';

export const SPEC_PREFIX = 'QUIZ';
/** Arcade initials: exactly three capital letters. */
export const INITIALS_RE = /^[A-Z]{3}$/;
export const PHASES = ['lobby', 'pre-round', 'question', 'reveal', 'final'] as const;
export const ANSWER_SLOTS = ['A', 'B', 'X', 'Y'] as const;
export const PAUSE_MESSAGE = 'GAME PAUSED';

export type Phase = (typeof PHASES)[number];
export type AnswerSlot = (typeof ANSWER_SLOTS)[number];
export type Tone = 'neutral' | 'success' | 'error';
export type AnswerFeedback = {
  type: 'controller-feedback';
  message: string;
  selected: AnswerSlot | null;
  locked: boolean;
  tone: Tone;
};

/** The name as stored from phone context: initials or nothing. */
export function acceptName(name: unknown): string | undefined {
  return typeof name === 'string' && INITIALS_RE.test(name) ? name : undefined;
}

export function playerName(p: number, ai: boolean, name?: string): string {
  if (ai) return `P${p + 1} · BOT`;
  return name ? `P${p + 1} · ${name}` : `P${p + 1}`;
}

/** The addressed player spec: the listing profile under a per-slot name. */
export function playerSpec(base: ControllerSpec, p: number): ControllerSpec {
  return { ...base, name: `${SPEC_PREFIX} · P${p + 1}` };
}

export function pauseMessage(): { type: 'controller-feedback'; message: string; locked: boolean } {
  return { type: 'controller-feedback', message: PAUSE_MESSAGE, locked: true };
}

export function answerFeedback(input: {
  phase: string;
  /** Index of the picked option, or null. */
  pick: number | null;
  lastCorrect: boolean;
  lastDelta: number;
  score: number;
}): AnswerFeedback {
  const phase = input.phase;
  const selected = input.pick === null ? null : (ANSWER_SLOTS[input.pick] ?? null);
  let message = 'Press an answer button to start';
  let locked = false;
  let tone: Tone = 'neutral';
  if (phase === 'pre-round') { message = 'Get ready…'; locked = true; }
  if (phase === 'question') { message = selected ? `ANSWER ${selected} LOCKED` : 'Choose your answer'; locked = selected !== null; }
  if (phase === 'reveal') { message = input.lastCorrect ? `CORRECT · +${input.lastDelta} points` : selected ? 'INCORRECT' : 'NO ANSWER'; locked = true; tone = input.lastCorrect ? 'success' : 'error'; }
  if (phase === 'final') { message = `GAME COMPLETE · ${input.score} points`; locked = true; }
  return { type: 'controller-feedback', message, selected, locked, tone };
}

/** Tournament label for a player's final quiz score. */
export function scoreLabel(score: number, top: number): string {
  return score === top ? `1st · ${score} right` : `${score} right`;
}

/** Everything the game can emit, for a validator test. `base` is the listing profile. */
export function enumerateControllerStrings(base?: ControllerSpec): {
  specs: ControllerSpec[]; feedback: string[]; scoreLabels: string[];
  spectatorViews: { title: string; leaderboard: { p: number; label: string; score: number; colour: string; tournamentTotal?: number }[]; highlight?: string }[];
} {
  const specs: ControllerSpec[] = [];
  const fallback: ControllerSpec = base ?? { name: SPEC_PREFIX, version: 1, slots: {} } as unknown as ControllerSpec;
  if (base) specs.push(base);
  for (let p = 0; p < 8; p++) specs.push(playerSpec(fallback, p));
  const feedback = new Set<string>([PAUSE_MESSAGE]);
  const picks: (number | null)[] = [null, 0, 1, 2, 3];
  const scores = [0, 1, 5, 10, 25, 100, 200];
  const deltas = [0, 1, 10, 100, 500, 1000];
  for (const phase of PHASES) {
    for (const pick of picks) {
      for (const lastCorrect of [false, true]) {
        for (const lastDelta of deltas) {
          for (const score of scores) feedback.add(answerFeedback({ phase, pick, lastCorrect, lastDelta, score }).message);
        }
      }
    }
  }
  const scoreLabels = new Set<string>();
  for (const score of scores) {
    if (score === 0) continue;
    scoreLabels.add(scoreLabel(score, score));
    scoreLabels.add(scoreLabel(score, score + 1));
  }
  return { specs, feedback: [...feedback], scoreLabels: [...scoreLabels], spectatorViews: [] };
}
